import { useState } from 'react';
import type { RemovalReason, RemovalRequest } from '@photos/shared';
import { useRequestRemoval } from '../../api/hooks';
import { useToast } from '../../state/toasts';
import { Icon } from '../Icon';
import { Modal } from '../Modal';

const NOTE_MAX = 280;

export const REMOVAL_REASONS: { value: RemovalReason; label: string }[] = [
  { value: 'me-or-my-child', label: 'This is me / my child and I’d like it removed' },
  { value: 'inappropriate', label: 'Unflattering / inappropriate' },
  { value: 'duplicate-or-poor', label: 'Duplicate or poor quality' },
  { value: 'other', label: 'Other' },
];

export function removalReasonLabel(r: RemovalReason): string {
  return REMOVAL_REASONS.find((x) => x.value === r)?.label ?? r;
}

/** The reason as reviewers read it: "Other: <their words>" for Other. */
export function removalReasonText(r: Pick<RemovalRequest, 'reason' | 'note'>): string {
  return r.reason === 'other' ? `Other: ${r.note}` : removalReasonLabel(r.reason);
}

/** "Request removal" from the lightbox. The photo is hidden right away while the media team reviews the request. */
export function RemovalDialog({ photoId, onClose, onDone }: { photoId: string; onClose(): void; onDone(): void }) {
  const [reason, setReason] = useState<RemovalReason>('me-or-my-child');
  const [note, setNote] = useState('');
  const req = useRequestRemoval();
  const toast = useToast();
  const other = reason === 'other';
  const missing = other && !note.trim();

  const send = () =>
    req.mutate(
      { photoId, body: { reason, note: note.trim() } },
      {
        onSuccess: () => {
          onClose();
          toast({ icon: 'check', title: 'Removal request sent', sub: 'The media team has been notified.' });
          onDone();
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t send the request', sub: e.message }),
      },
    );

  return (
    <Modal onClose={onClose} labelledBy="rr-title">
      <h3 id="rr-title">Request removal</h3>
      <p className="help">
        The media team will review this within a few days. The photo is hidden from search right away while your request is
        reviewed.
      </p>
      <div className="field">
        <label htmlFor="rr-reason">Reason</label>
        <select id="rr-reason" className="inp" value={reason} onChange={(e) => setReason(e.target.value as RemovalReason)}>
          {REMOVAL_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="rr-note">
          {other ? 'Tell us why' : 'Anything else?'}{' '}
          {!other && <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</small>}
        </label>
        <textarea
          id="rr-note"
          className="inp"
          rows={3}
          maxLength={NOTE_MAX}
          required={other}
          aria-invalid={missing || undefined}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <small className="counter" aria-live="polite">
          {note.length}/{NOTE_MAX}
        </small>
      </div>
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn warn-fill" disabled={req.isPending || missing} onClick={send}>
          <Icon name="flag" />
          Send request
        </button>
      </div>
    </Modal>
  );
}
